// Copyright: Sean O'Donoghue
// eslint-disable-next-line

const log = (msg) => process.env.VUE_APP_DEV && console.log(`useProgram: ${msg}`);

import {useRoute} from "vue-router";
import {DateTime} from "luxon";
import stringify from "json-stringify-safe";
import timezoneHistory from "@/assets/history.json";
import {getPreferredTimeZone} from "@/composables/selectedTimeZones";
import {fetchProgramSheet} from "@/composables/useProgramSheet";
import {getLocalNote} from "@/composables/useLocalEventDates";

// Speaker statuses shown after the name, e.g. "Vera Williams (TBC)" (exact match only)
const SHOWN_STATUSES = ["TBC", "TBD"];
const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const programs = {};
let previousEventEndTime = "";
let previousEventDayTitle = "";
let sessionStartEvent = "";

export async function getProgram(year) {
  if (!year) return {};
  try {
    const program = (await fetchProgramSheet(year)).program;
    if (!program) return []; // the sheet didn't load (or the year has none): no rows
    // the program export sometimes loses the "Date" heading (first cell);
    // without it every row is skipped, so restore it rather than render nothing
    if (program?.[0]?.[0] === "") {
      console.warn(`Program for ${year} is missing the "Date" heading; assuming column 0`);
      program[0][0] = "Date";
    }
    return program;
  } catch (error) {
    console.error(`Fetching program for ${year}: ${error}`);
    return [];
  }
}

function timeZoneConference(year) {
  if (year) return timezoneHistory.years[year];
  return "";
}

// Default display timezone: the viewer's preferred zone (selectedTimeZones.js: the account's last
// choice, else this browser's, else the browser's own; decided 2026-10-13: no longer the event's
// zone before an in-person meeting). Async as before; year unused, kept for callers.
export async function getDefaultTimeZone(year, user) { // eslint-disable-line no-unused-vars
  return getPreferredTimeZone(user);
}

function getNameOfWeekday(weekdayNum) {
  let days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  if (weekdayNum >= 0 && weekdayNum <= days.length) return days[weekdayNum - 1];
}

// on the 1st, so a month shorter than today's date doesn't roll over (e.g. April on 31 October)
function monthNumberToName(monthNumber) {
  const date = new Date(2000, monthNumber - 1, 1);
  return date.toLocaleString("en-US", {month: "long"});
}

// Program data from 2015 and earlier sometimes omits the Date field on rows that
// continue within the same day (e.g. short "Breakfast" rows with only 4 elements).
// This helper maintains running date state so those rows inherit the most recently
// seen date. From 2016 onward every row carries an explicit Date, so this fallback
// is a no-op for those years.
function resolveLegacyDate(row, options) {
  const {col} = options;
  const date = row[col("Date")];
  if (date) {
    options.previousEventDate = date;
    return date;
  }
  return options.previousEventDate || null;
}

function parseDate(row, options) {
  const date = resolveLegacyDate(row, options);
  log(`Program/Parse: date = ${date}`);
  return {date};
}

// dd/mm/yyyy -> sortable number (yyyymmdd)
function dateSortKey(date) {
  const [d, m, y] = date.split("/").map(Number);
  return y * 10000 + m * 100 + d;
}

// dateFrom/dateTo (dd/mm/yyyy, inclusive) limit a table to a single event
// when the same session category recurs later in the year
function outsideDateRange(date, options) {
  const {dateFrom, dateTo} = options;
  if (!date || (!dateFrom && !dateTo)) return false;
  const key = dateSortKey(date);
  if (dateFrom && key < dateSortKey(dateFrom)) return true;
  if (dateTo && key > dateSortKey(dateTo)) return true;
  return false;
}

function calculateEventTime(row, date, options) {
  if (!date) return {}; // no date available (legacy rows without a Date field)
  date = date.split("/");
  if (date.length !== 3) return {};
  const {col, timeZoneConference, timeZoneSelected} = options;
  let eventTime = row[col("Duration/Time")];
  let start, end, duration;
  if (eventTime && eventTime.match("-")) {
    [start, end] = eventTime.split(/\s*-\s*/).map((t) => t.split(":"));
    log(`Event start: ${start}, end: ${end}`);
    const commonDateProps = {year: date[2], month: date[1], second: 0};
    start = DateTime.fromObject(
      {...commonDateProps, day: date[0], hour: start[0], minute: start[1]},
      {zone: timeZoneConference}
    );
    end = DateTime.fromObject(
      {...commonDateProps, day: date[0], hour: end[0], minute: end[1]},
      {zone: timeZoneConference}
    );
    // any zone chosen in the menu (the browser's, or a remembered one: selectedTimeZones.js)
    if (timeZoneSelected && timeZoneSelected !== timeZoneConference) {
      start = start.setZone(timeZoneSelected);
      end = end.setZone(timeZoneSelected);
    }
    return {start, end};
  }
  if (eventTime) {
    duration = parseInt(eventTime);
    if (!previousEventEndTime) {
      console.error(`Need a previous event`);
      return {};
    }
    const start = previousEventEndTime;
    const end = start.plus({minute: duration});
    return {start, end};
  }
  const title = row[col("Talk/Role/Event")];
  if (title.match(/^End of /i)) return {start: previousEventEndTime};
  if (title.match(/^Bus /i)) return {end: ""};
  return {};
}

// A speaker's status applies to all their entries on that date (e.g. both the talk and the
// breakout on 3 November), but not to other dates (e.g. an April talk that was confirmed)
function getStatusKey(row, col) {
  return `${String(row[col("Date")] || "").trim()}|${String(row[col("Name")] || "").trim()}`;
}

function getStatusByName(program, col) {
  const statusByName = {};
  for (const row of program.slice(1)) {
    const status = String(row[col("Status")] || "").trim();
    if (!row[col("Name")] || !SHOWN_STATUSES.includes(status)) continue;
    statusByName[getStatusKey(row, col)] = status;
  }
  return statusByName;
}

function setEventProperties(row, col, i, statusByName = {}) {
  const event = {};
  event.ref = `event-item-${i}`;
  event.video = row[col("Video")];
  event.mode = event.video ? "Recorded video" : row[col("Mode")];
  if (event.video) log(`Found video=${event.video}; mode=${event.mode}`);
  event.title = row[col("Talk/Role/Event")];
  const name = row[col("Name")];
  event.name = name && name.includes("?") ? "T.B.A" : name;
  event.status = statusByName[getStatusKey(row, col)] || "";
  event.url = row[col("URL")];
  event.description =
    row[col("Description")] ||
    (row[col("Category")].match(/Breakout/i)
      ? "All VIZBI participants were invited to propose and participate in two <a href='https://en.wikipedia.org/wiki/Unconference'>unconference</a>-style discussion sessions."
      : "");
  return event;
}

function setEventType(row, col) {
  const event = {};
  const title = row[col("Talk/Role/Event")];
  if (title.match(/^Session/)) {
    event.type = "session";
    event.title = row[col("Category")];
    if (row[col("Name")]) {
      event.subtitle = title;
    } else if (row[col("Venue")]) {
      event.subtitle = "Venue";
      event.venue = row[col("Venue")];
    }
  } else {
    event.type = row[col("Category")].match(/Keynote/i) ? "keynote" : "speaker";
    event.title = title;
  }
  return event;
}

// Each conference day stays one day in the viewer's zone, dated by its first session's start on
// their calendar (not the check-in), as on the /2026 pages (useLocalEventDates.js; decided
// 2026-10-13): in Sydney the Masterclass of 14 April (09:00 London) stays one day, "Apr 14", rather
// than splitting at local midnight. {"14/04/2026": DateTime of that first start, in the zone}
const CHECK_IN = /check-in|platform q&a/i;

function getDayAnchors(program, col, options) {
  const anchors = {};
  let date = "";
  for (const row of program.slice(1)) {
    date = row[col("Date")] || date;
    const time = row[col("Duration/Time")] || "";
    if (!date || anchors[date] || !time.includes("-")) continue;
    if (CHECK_IN.test(row[col("Talk/Role/Event")] || "")) continue;
    const {start} = calculateEventTime(row, date, options);
    if (start?.isValid) anchors[date] = start;
  }
  return anchors;
}

// "Sydney local time" when the day falls on another date in the viewer's zone than in the
// program's (dayStart: the day's first session, in that zone; date: "18/11/2026"), else ""
function getDayNote(dayStart, date, options) {
  const [day, month, year] = String(date || "").split("/").map(Number);
  const isSame = dayStart.day === day && dayStart.month === month && dayStart.year === year;
  return isSame ? "" : getLocalNote(options.timeZoneSelected);
}

// a session: one category ("Masterclass"), or a pattern for several (the Masterclass page's
// in-person, virtual and online Masterclasses: MasterclassTables.vue)
function getIsOtherSession(session, category) {
  if (!session) return false;
  if (session instanceof RegExp) return !session.test(category || "");
  return category !== session;
}

function processRow(acc, row, i, options) {
  if (i === 0) return acc;
  const {col, session} = options;
  const role = row[col("Talk/Role/Event")];
  let skip = false;
  let {date} = parseDate(row, options);
  log(`start loop i = ${i}`);
  log(`previousEventEndTime = ${previousEventEndTime}`);
  if (!role) return acc;
  if (days.includes(role)) previousEventDayTitle = row[col("Category")];
  const event = setEventProperties(row, col, i, options.statusByName);
  Object.assign(event, calculateEventTime(row, date, options));
  Object.assign(event, setEventType(row, col));
  if (getIsOtherSession(session, row[col("Category")])) skip = true;
  if (outsideDateRange(date, options)) skip = true;
  if (event.type === "session" && !skip) sessionStartEvent = event;
  if (!event.start) return acc;
  const dayStart = options.dayAnchors?.[date] || event.start; // the conference day, kept whole
  const weekday = getNameOfWeekday(dayStart.weekday);
  // the day's key, also in the URL hash ("Apr-14"): month and day, so no two dates of a program
  // share one (a weekday and day of the month could)
  const day = `${monthNumberToName(dayStart.month).slice(0, 3)} ${dayStart.day}`;
  log(`day = ${day}`);
  previousEventEndTime = event.end ? event.end : event.start;
  if (skip) return acc;
  const localNote = getDayNote(dayStart, date, options);
  date = `${weekday}, ${monthNumberToName(dayStart.month)} ${dayStart.day}, ${dayStart.year}`;
  const sessionTitle = typeof session === "string" ? session : "";
  const title = sessionTitle || acc[day]?.title || "";
  log(`[day, date, title] = [${day}, ${date}, ${title}]`);
  log(`acc[day] = ${acc[day] || "something"}`);
  acc[day] = acc[day] || {date, events: [], title, localNote};
  log(`after assignment, acc[day] = ${JSON.stringify(acc[day])}`);
  if (sessionStartEvent) {
    acc[day].events.push(sessionStartEvent);
    sessionStartEvent = "";
  }
  acc[day].events.push(event);
  if (acc[day] && !acc[day].title) acc[day].title = previousEventDayTitle;
  return acc;
}

function updatedProgram(options) {
  log(`updatedProgram()`);
  const {program} = options;
  log(`program = ${JSON.stringify(program)}`);
  if (!program || !program.length || program.length === 0) return {};
  log(`program.length = ${program.length}`);
  const col = (heading) => program[0].indexOf(heading);
  options.col = col;
  options.statusByName = getStatusByName(program, col);
  options.dayAnchors = getDayAnchors(program, col, options);
  const output = program.reduce((acc, row, i) => processRow(acc, row, i, options), {});
  log(`output = ${JSON.stringify(output)}`);
  return output;
}

function getYearFromRouteOrEmpty() {
  try {
    const route = useRoute();
    return route?.params?.year || "";
  } catch (error) {
    return "";
  }
}

export default async function useProgram(options) {
  log(`options = ${stringify(options)}`);
  const {timeZoneSelected = "", year = ""} = options;
  if (!timeZoneSelected) return {};
  const resolvedYear = year || getYearFromRouteOrEmpty();
  if (!resolvedYear) return {};
  log(`year = ${resolvedYear}`);
  const program = programs[resolvedYear] || (await getProgram(resolvedYear));
  if (Object.keys(program).length === 0) return {};
  options.timeZoneConference = timeZoneConference(resolvedYear);
  options.program = program;
  return updatedProgram(options);
}
